import { useQuery } from '@tanstack/react-query'
import { FileText, Loader2 } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { fidelizacionApi } from '../api/fidelizacion.api'
import { formatMoneda, formatNumero, terminoComprobante, esPeru } from '../lib/paisMapa'

interface Props {
  configId: number
  facturaId: number | string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

function fmtQty(v: unknown, configId: number): string {
  if (v === null || v === undefined || v === '') return '—'
  const n = typeof v === 'number' ? v : parseFloat(String(v))
  if (Number.isNaN(n)) return String(v)
  return formatNumero(n, configId, { maximumFractionDigits: 2 })
}

function fmtDate(v: unknown): string {
  if (!v) return '—'
  const s = String(v)
  const soloFecha = s.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (soloFecha) return `${soloFecha[3]}/${soloFecha[2]}/${soloFecha[1]}`
  try {
    return new Date(s).toLocaleString('es-EC', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'America/Guayaquil',
    })
  } catch {
    return s
  }
}

function estadoBadge(estado: unknown) {
  const s = String(estado || '').toLowerCase()
  if (s === 'validada' || s === 'corregida' || s === 'aprobada') {
    return <Badge className="bg-emerald-600 hover:bg-emerald-700 text-white">{String(estado)}</Badge>
  }
  if (s === 'rechazada') {
    return <Badge className="bg-red-600 hover:bg-red-700 text-white">{String(estado)}</Badge>
  }
  return <Badge variant="outline">{String(estado || '—')}</Badge>
}

export function FacturaPreviewDialog({ configId, facturaId, open, onOpenChange }: Props) {
  const enabled = open && facturaId !== null && configId > 0
  const query = useQuery({
    queryKey: ['fidelizacion', 'factura-detalle', configId, facturaId],
    queryFn: () => fidelizacionApi.getListadoItem(configId, 'facturas', facturaId!),
    enabled,
    staleTime: 60 * 1000,
  })

  const f = query.data as Record<string, unknown> | undefined
  const productos = (f?.productos as Array<Record<string, unknown>> | undefined) ?? []

  const fmtMoney = (v: unknown) =>
    formatMoneda(v as number | string | null | undefined, configId)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {terminoComprobante(configId, { capitalize: true })} {f?.numero_factura ? <span className="font-mono">#{String(f.numero_factura)}</span> : ''}
          </DialogTitle>
        </DialogHeader>

        {query.isLoading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : query.error ? (
          <p className="text-sm text-red-500 py-8 text-center">
            {esPeru(configId)
              ? `No se pudo cargar el ${terminoComprobante(configId)}.`
              : 'No se pudo cargar la factura.'}
          </p>
        ) : !f ? null : (
          <div className="space-y-4 text-sm">
            {/* Cabecera tipo factura */}
            <div className="rounded-md border border-gray-300 dark:border-gray-700 p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-muted-foreground">Establecimiento</p>
                  <p className="font-medium">{String(f.establecimiento || '—')}</p>
                  {f.sucursal ? (
                    <p className="text-xs text-muted-foreground">Suc: {String(f.sucursal)}</p>
                  ) : null}
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Estado</p>
                  {estadoBadge(f.estado)}
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-muted-foreground">Fecha emisión (OCR)</p>
                  <p>{fmtDate(f.fecha_emision)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Fecha carga</p>
                  <p>{fmtDate(f.fecha_carga)}</p>
                </div>
              </div>

              <Separator />

              {/* Proveedor (emisor de la factura) */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-muted-foreground">RUC proveedor</p>
                  <p className="font-mono text-xs">{String(f.ruc_proveedor || '—')}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Nombre proveedor</p>
                  <p className="text-xs">{String(f.establecimiento || '—')}</p>
                </div>
              </div>

              {/* Cliente: datos personales del consumidor — no se recopilan. */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-muted-foreground">RUC cliente</p>
                  <p className="text-xs italic text-muted-foreground">No se recopila</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Nombre cliente</p>
                  <p className="text-xs italic text-muted-foreground">No se recopila</p>
                </div>
              </div>
            </div>

            {/* Detalle de productos */}
            <div className="rounded-md border border-gray-300 dark:border-gray-700 overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-gray-100 dark:bg-[hsl(220,15%,16%)]">
                  <tr>
                    <th className="text-left p-2">Producto</th>
                    <th className="text-right p-2">Cant.</th>
                    <th className="text-right p-2">Unitario</th>
                    <th className="text-right p-2">Total</th>
                    <th className="text-right p-2">Pts</th>
                  </tr>
                </thead>
                <tbody>
                  {productos.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="p-3 text-center text-muted-foreground">
                        Sin productos.
                      </td>
                    </tr>
                  ) : (
                    productos.map((p, i) => (
                      <tr
                        key={i}
                        className={`border-t border-gray-200 dark:border-gray-700 ${
                          p.es_participante
                            ? 'bg-emerald-500/10 dark:bg-emerald-500/15 text-emerald-900 dark:text-emerald-100'
                            : 'opacity-60'
                        }`}
                      >
                        <td
                          className={`p-2 ${
                            p.es_participante
                              ? 'border-l-2 border-emerald-500'
                              : ''
                          }`}
                        >
                          <div className="font-medium">{String(p.nombre || '—')}</div>
                          <div className="text-[10px] text-muted-foreground">
                            {String(p.unidad_facturacion || '')}
                            {p.es_participante
                              ? ' · producto del programa'
                              : ' · fuera del programa'}
                          </div>
                        </td>
                        <td className="p-2 text-right tabular-nums">{fmtQty(p.cantidad, configId)}</td>
                        <td className="p-2 text-right tabular-nums">{fmtMoney(p.precio_unitario)}</td>
                        <td className="p-2 text-right tabular-nums">{fmtMoney(p.precio_total)}</td>
                        <td
                          className={`p-2 text-right tabular-nums ${
                            p.es_participante ? 'font-semibold text-emerald-600 dark:text-emerald-400' : ''
                          }`}
                        >
                          {fmtQty(p.puntos_calculados, configId)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Totales */}
            <div className="rounded-md border border-gray-300 dark:border-gray-700 p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="tabular-nums">{fmtMoney(f.subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">IVA</span>
                <span className="tabular-nums">{fmtMoney(f.iva)}</span>
              </div>
              {f.descuento_total ? (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Descuento</span>
                  <span className="tabular-nums">{fmtMoney(f.descuento_total)}</span>
                </div>
              ) : null}
              <Separator />
              <div className="flex justify-between font-semibold">
                <span>{`Total ${terminoComprobante(configId)}`}</span>
                <span className="tabular-nums">{fmtMoney(f.monto)}</span>
              </div>
              <div className="flex justify-between text-emerald-700 dark:text-emerald-400">
                <span>Monto del programa</span>
                <span className="tabular-nums">{fmtMoney(f.monto_participantes)}</span>
              </div>
              <Separator />
              <div className="flex justify-between">
                <span className="text-muted-foreground">Puntos estimados</span>
                <span className="tabular-nums">{fmtQty(f.puntos_estimados, configId)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Puntos otorgados</span>
                <span className="tabular-nums">{fmtQty(f.puntos_otorgados, configId)}</span>
              </div>
              {f.motivo_rechazo ? (
                (() => {
                  const esCorreccion =
                    typeof f.estado === 'string' && f.estado.toLowerCase() === 'corregida'
                  return (
                    <>
                      <Separator />
                      <div className={esCorreccion ? 'text-amber-600 dark:text-amber-500' : 'text-red-500'}>
                        <p className="text-xs uppercase tracking-wide">
                          {esCorreccion ? 'Motivo de corrección' : 'Motivo rechazo'}
                        </p>
                        <p>{String(f.motivo_rechazo)}</p>
                      </div>
                    </>
                  )
                })()
              ) : null}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
